/**
 * In-Store Dashboard Page
 * 
 * Central hub for all in-store workflows:
 * - Partner Try-On Studio (staff-assisted)
 * - Customer Try-On Sessions (self-service QR)
 * - Activity monitoring (Partner Usage & Customer Sessions)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Wand2, QrCode, Store,
  Eye, Loader2,
  CheckCircle, XCircle, Image as ImageIcon,
  X, Copy, Check, ExternalLink, Settings, 
  ToggleLeft, ToggleRight, Printer, Info
} from 'lucide-react';
import { publicStudioAPI, operationsAPI, partnerAPI } from '../../api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import InStoreTryOnTab from '../../components/customer-engagement/InStoreTryOnTab';
import InstoreQRPoster from '../../components/InstoreQRPoster';

// ==================== Action Card Component ====================
const ActionCard = ({ icon: Icon, title, description, buttonText, onClick, variant = 'primary', disabled = false }) => {
  const isPrimary = variant === 'primary';
  
  return (
    <div className={`bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-all ${disabled ? 'opacity-60' : ''}`}>
      <div className="flex items-start gap-4">
        <div className={`p-3 rounded-xl ${isPrimary ? 'bg-blue-100' : 'bg-slate-100'}`}>
          <Icon className={`w-6 h-6 ${isPrimary ? 'text-blue-600' : 'text-slate-600'}`} />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-slate-900 text-lg">{title}</h3>
          <p className="text-sm text-slate-500 mt-1">{description}</p>
          <button
            onClick={onClick}
            disabled={disabled}
            className={`mt-4 px-5 py-2.5 rounded-lg font-medium text-sm transition-colors ${
              disabled 
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : isPrimary 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
            data-testid={`action-${title.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {buttonText}
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== KPI Card Component ====================
const KPICard = ({ icon: Icon, label, value, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    amber: 'bg-amber-100 text-amber-600',
    purple: 'bg-purple-100 text-purple-600'
  };
  
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div>
          <p className="text-xl font-bold text-slate-900">{value}</p>
          <p className="text-xs text-slate-500">{label}</p>
        </div>
      </div>
    </div>
  );
};

// ==================== Partner Usage Tab ====================
const PartnerUsageTab = () => {
  const [usageData, setUsageData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0 });

  useEffect(() => {
    const fetchUsageData = async () => {
      setLoading(true);
      try {
        // Fetch operations with source = TRYON_STUDIO
        const response = await operationsAPI.list({ 
          page_size: 50,
          source: 'TRYON_STUDIO'
        });
        
        const operations = response.data?.operations || [];
        setUsageData(operations);
        setStats({ total: response.data?.total || operations.length });
      } catch (error) {
        console.error('Failed to fetch partner usage:', error);
        // Don't show error toast, just show empty state
      } finally {
        setLoading(false);
      }
    };
    fetchUsageData();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Simple KPI */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KPICard icon={Eye} label="Total Try-Ons" value={stats.total} color="blue" />
      </div>

      {/* Usage Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Timestamp</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Garment</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Result</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {usageData.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-12 text-center text-slate-400">
                    <ImageIcon className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                    <p>No partner try-on activity yet</p>
                    <p className="text-xs mt-1">Use the Partner Try-On Studio to get started</p>
                  </td>
                </tr>
              ) : (
                usageData.map((item, index) => (
                  <tr key={item.operation_id || item.execution_id || index} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {new Date(item.created_at || item.timestamp).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                        item.api_type === 'MOMENTS' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {item.api_type === 'MOMENTS' ? 'Moment' : 'Try-On'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-900">
                      {item.product_name || item.sku_id || '-'}
                    </td>
                    <td className="px-4 py-3">
                      {item.status === 'SUCCESS' || item.output_image_url || item.result_url ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
                          <CheckCircle className="w-3 h-3" /> Generated
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
                          <XCircle className="w-3 h-3" /> No
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// CustomerSessionsTab removed - now using InStoreTryOnTab component imported from customer-engagement

// ==================== Main In-Store Page ====================
const InStore = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('customer-sessions');
  const [instoreMode, setInstoreMode] = useState(null);
  const [loadingMode, setLoadingMode] = useState(true);
  
  // QR Modal states
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [fixedLink, setFixedLink] = useState(null);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [togglingLink, setTogglingLink] = useState(false);
  const [copied, setCopied] = useState('');
  const [showQRPoster, setShowQRPoster] = useState(false);
  const [partnerInfo, setPartnerInfo] = useState(null);

  // Fetch fixed link data
  const fetchFixedLink = async () => {
    setLoadingLinks(true);
    try {
      const [linkResponse, profileResponse] = await Promise.all([
        publicStudioAPI.getFixedLink(),
        partnerAPI.getProfile()
      ]);
      setFixedLink(linkResponse.data);
      setPartnerInfo(profileResponse.data);
      setInstoreMode(linkResponse.data?.status === 'active');
    } catch (error) {
      console.error('Failed to fetch fixed link:', error);
      setInstoreMode(false);
    } finally {
      setLoadingLinks(false);
      setLoadingMode(false);
    }
  };

  // Check in-store mode status on mount
  useEffect(() => {
    fetchFixedLink();
  }, []);

  // Fetch link data when QR modal opens
  useEffect(() => {
    if (qrModalOpen && !fixedLink) {
      fetchFixedLink();
    }
  }, [qrModalOpen, fixedLink]);

  // Toggle In-Store Mode
  const toggleInstoreMode = async () => {
    setTogglingLink(true);
    try {
      const response = await publicStudioAPI.toggleFixedLink();
      const newStatus = response.data.status === 'active';
      toast.success(`In-Store Mode ${newStatus ? 'enabled' : 'disabled'}`);
      setInstoreMode(newStatus);
      setFixedLink(prev => prev ? { ...prev, status: response.data.status } : null);
    } catch (error) {
      toast.error('Failed to toggle In-Store Mode');
    } finally {
      setTogglingLink(false);
    }
  };

  const handleOpenPartnerStudio = () => {
    navigate('/partners/tryon');
  };

  const handleOpenCustomerTryOn = () => {
    if (!instoreMode) {
      toast.error('Please enable In-Store Mode first');
      return;
    }
    setQrModalOpen(true);
  };

  const copyLinkToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      setTimeout(() => setCopied(''), 2000);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Failed to copy');
    }
  };

  const tabs = [
    { id: 'customer-sessions', label: 'Customer Sessions' },
    { id: 'partner-usage', label: 'Partner Usage' }
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">In-Store</h1>
          <p className="text-slate-500 text-sm mt-1">Manage in-store try-on experiences</p>
        </div>
        
        {/* In-Store Mode Toggle */}
        {!loadingMode && (
          <button
            onClick={toggleInstoreMode}
            disabled={togglingLink}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              instoreMode 
                ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' 
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
            data-testid="instore-mode-toggle"
          >
            {togglingLink ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : instoreMode ? (
              <ToggleRight className="w-5 h-5" />
            ) : (
              <ToggleLeft className="w-5 h-5" />
            )}
            <Store className="w-4 h-4" />
            In-Store Mode: {instoreMode ? 'Active' : 'Inactive'}
          </button>
        )}
      </div>

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <ActionCard
          icon={Wand2}
          title="Partner Try-On Studio"
          description="Assist customers directly in-store"
          buttonText="Open Partner Try-On Studio"
          onClick={handleOpenPartnerStudio}
          variant="primary"
        />
        <ActionCard
          icon={QrCode}
          title="In-Store Customer Try-On"
          description={instoreMode ? "Let customers scan QR and try on themselves" : "Enable In-Store Mode to allow customer self-service"}
          buttonText="Open In-Store Customer Try-On"
          onClick={handleOpenCustomerTryOn}
          variant="secondary"
          disabled={!instoreMode}
        />
      </div>

      {/* In-Store Activity Section */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {/* Section Header */}
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="font-semibold text-slate-900">In-Store Activity</h2>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 px-6">
          <div className="flex gap-6">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'customer-sessions' && <InStoreTryOnTab displayMode="list" />}
          {activeTab === 'partner-usage' && <PartnerUsageTab />}
        </div>
      </div>

      {/* QR Code Modal */}
      <AnimatePresence>
        {qrModalOpen && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
            onClick={() => setQrModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 100 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 100 }}
              className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[95vh] sm:max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-3 sm:p-4 border-b border-slate-200 sticky top-0 bg-white z-10">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg sm:rounded-xl">
                    <QrCode className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-base sm:text-lg font-semibold text-slate-900">In-Store TryOn Mode</h2>
                    <p className="text-[10px] sm:text-xs text-slate-500">ONE permanent QR code for your store</p>
                  </div>
                </div>
                <button 
                  onClick={() => setQrModalOpen(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg"
                  data-testid="close-qr-modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-3 sm:p-4 overflow-y-auto" style={{ maxHeight: 'calc(95vh - 60px)' }}>
                {loadingLinks ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  </div>
                ) : fixedLink ? (
                  <div className="space-y-3 sm:space-y-4">
                    {/* Fixed Link Card - Mobile: Stack vertically, Desktop: Side by side */}
                    <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-3 sm:p-5 border border-purple-200">
                      <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3 sm:gap-4">
                        {/* QR Code - Centered on mobile */}
                        <div className="flex-shrink-0 bg-white p-2 rounded-lg shadow-sm">
                          <img 
                            src={fixedLink.qr_url}
                            alt="QR Code"
                            className="w-32 h-32 sm:w-28 sm:h-28"
                            data-testid="instore-qr-code"
                          />
                        </div>
                        
                        {/* Link Info */}
                        <div className="flex-1 min-w-0 w-full text-center sm:text-left">
                          <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
                            <span className="font-semibold text-slate-900">Your Store QR</span>
                            <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                              fixedLink.status === 'active' 
                                ? 'bg-emerald-100 text-emerald-700' 
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {fixedLink.status === 'active' ? 'Active' : 'Disabled'}
                            </span>
                          </div>
                          
                          {/* Stats - Grid on mobile */}
                          <div className="grid grid-cols-3 sm:grid-cols-1 gap-2 sm:gap-1.5 text-xs text-slate-600 mb-3">
                            <p className="flex flex-col sm:flex-row items-center sm:items-center gap-1 sm:gap-1.5">
                              <span className="w-6 h-6 sm:w-4 sm:h-4 bg-blue-100 rounded flex items-center justify-center text-sm sm:text-xs">📊</span>
                              <span className="text-[10px] sm:text-xs">{fixedLink.total_sessions || 0} Sessions</span>
                            </p>
                            <p className="flex flex-col sm:flex-row items-center sm:items-center gap-1 sm:gap-1.5">
                              <span className="w-6 h-6 sm:w-4 sm:h-4 bg-purple-100 rounded flex items-center justify-center text-sm sm:text-xs">⏱️</span>
                              <span className="text-[10px] sm:text-xs">{fixedLink.session_timeout_hours}h Duration</span>
                            </p>
                            <p className="flex flex-col sm:flex-row items-center sm:items-center gap-1 sm:gap-1.5">
                              <span className="w-6 h-6 sm:w-4 sm:h-4 bg-pink-100 rounded flex items-center justify-center text-sm sm:text-xs">👗</span>
                              <span className="text-[10px] sm:text-xs">{fixedLink.tryons_per_session} Try-ons</span>
                            </p>
                          </div>
                          
                          {/* Enable/Disable Button */}
                          <button
                            onClick={toggleInstoreMode}
                            disabled={togglingLink}
                            data-testid="toggle-instore-mode-btn"
                            className={`w-full py-2.5 sm:py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                              fixedLink.status === 'active'
                                ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                                : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                            }`}
                          >
                            {togglingLink ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : fixedLink.status === 'active' ? (
                              <>
                                <ToggleRight className="w-4 h-4" />
                                Disable In-Store Mode
                              </>
                            ) : (
                              <>
                                <ToggleLeft className="w-4 h-4" />
                                Enable In-Store Mode
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Link URL */}
                    <div className="bg-slate-50 rounded-xl p-3 sm:p-4 border border-slate-200">
                      <label className="block text-xs font-medium text-slate-500 mb-2">
                        Permanent Store Link
                      </label>
                      <div className="flex items-center gap-1.5 sm:gap-2 bg-white rounded-lg p-2 sm:p-2.5 border border-slate-200">
                        <input 
                          type="text"
                          value={fixedLink.link_url}
                          readOnly
                          className="flex-1 text-xs sm:text-sm text-slate-700 bg-transparent truncate font-mono min-w-0"
                        />
                        <button
                          onClick={() => copyLinkToClipboard(fixedLink.link_url, 'fixed')}
                          className="p-1.5 sm:p-2 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0"
                          data-testid="copy-instore-link-btn"
                        >
                          {copied === 'fixed' ? (
                            <Check className="w-4 h-4 text-emerald-600" />
                          ) : (
                            <Copy className="w-4 h-4 text-slate-400" />
                          )}
                        </button>
                        <a 
                          href={fixedLink.link_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 sm:p-2 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0"
                        >
                          <ExternalLink className="w-4 h-4 text-slate-400" />
                        </a>
                      </div>
                      <p className="text-[10px] sm:text-xs text-slate-400 mt-2">
                        This link never expires. Print the QR code once and use it forever.
                      </p>
                    </div>
                    
                    {/* Settings Link */}
                    <div className="bg-blue-50 rounded-xl p-3 sm:p-4 border border-blue-100">
                      <div className="flex items-start gap-2 sm:gap-3">
                        <Settings className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs sm:text-sm font-medium text-blue-800">Customize Settings</p>
                          <p className="text-[10px] sm:text-xs text-blue-600 mt-0.5">
                            Adjust session duration, try-on limits in{' '}
                            <button 
                              onClick={() => {
                                setQrModalOpen(false);
                                setActiveTab('customer-sessions');
                              }}
                              className="underline font-medium"
                            >
                              Customer Sessions → Settings
                            </button>
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Download Poster Button */}
                    <button
                      onClick={() => setShowQRPoster(true)}
                      data-testid="download-poster-btn"
                      className="w-full py-3 sm:py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium rounded-xl hover:opacity-90 transition-all flex items-center justify-center gap-2 shadow-lg text-sm sm:text-base"
                    >
                      <Printer className="w-4 h-4 sm:w-5 sm:h-5" />
                      Download Printable Poster
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <QrCode className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <h3 className="font-medium text-slate-900 mb-1">Loading your QR code...</h3>
                    <p className="text-sm text-slate-500">Please wait</p>
                  </div>
                )}
              </div>

              {/* Footer Info - Sticky on mobile */}
              <div className="p-3 sm:p-4 bg-slate-50 border-t border-slate-200">
                <div className="flex items-start gap-2 text-[10px] sm:text-xs text-slate-500">
                  <Info className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0 mt-0.5" />
                  <p>
                    <strong>How it works:</strong> Print this QR code and display it in your store. 
                    Customers scan it to get a private try-on session.
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* QR Poster Download Modal */}
      {showQRPoster && fixedLink && (
        <InstoreQRPoster
          qrUrl={fixedLink.qr_url}
          linkUrl={fixedLink.link_url}
          businessName={partnerInfo?.business_name || 'Your Store'}
          businessLogoUrl={partnerInfo?.logo_url}
          onClose={() => setShowQRPoster(false)}
        />
      )}
    </div>
  );
};

export default InStore;
